import type { PointerEvent } from "react";
import type { DesignElement } from "../../lib/design";
import type { Point } from "../../lib/vector";
import { useCanvasZoom } from "../canvas-camera";
export type VectorEditingProps = {
  id: string;
  point: number | null;
  zoom: number;
  onPointDown: (
    event: PointerEvent,
    id: string,
    index: number,
    handle?: "in" | "out",
  ) => void;
  onPointSelect: (index: number) => void;
};
export function VectorOverlay({
  element,
  editing,
}: {
  element: DesignElement;
  editing: VectorEditingProps;
}) {
  const zoom = useCanvasZoom(editing.zoom);
  const vector = element.vector!;
  const position = (point: Point) => ({
    x: (point.x * element.width) / vector.width,
    y: (point.y * element.height) / vector.height,
  });
  const active =
    editing.point === null ? undefined : vector.nodes[editing.point];
  return (
    <div className="vector-points">
      {active && (
        <svg
          className="vector-handle-lines"
          width={element.width}
          height={element.height}
          style={{ overflow: "visible" }}
          aria-hidden="true"
        >
          {(["in", "out"] as const).map(
            (handle) =>
              active[handle] && (
                <line
                  key={handle}
                  x1={position(active).x}
                  y1={position(active).y}
                  x2={position(active[handle]!).x}
                  y2={position(active[handle]!).y}
                  stroke="var(--selection)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              ),
          )}
        </svg>
      )}
      {vector.nodes.map((node, index) => (
        <button
          key={index}
          className={
            "vector-anchor " + (index === editing.point ? "active" : "")
          }
          aria-label={"Vector point " + (index + 1)}
          aria-pressed={index === editing.point}
          style={{
            left: position(node).x,
            top: position(node).y,
            transform: `translate(-50%,-50%) scale(${1 / zoom})`,
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
            editing.onPointDown(event, element.id, index);
          }}
          onClick={(event) => {
            event.stopPropagation();
            editing.onPointSelect(index);
          }}
        />
      ))}
      {active &&
        (["in", "out"] as const).map(
          (handle) =>
            active[handle] && (
              <button
                key={handle}
                className="vector-handle"
                aria-label={
                  handle === "in"
                    ? "Incoming curve handle"
                    : "Outgoing curve handle"
                }
                style={{
                  left: position(active[handle]!).x,
                  top: position(active[handle]!).y,
                  transform:
                    `translate(-50%,-50%) scale(${1 / zoom})`,
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  editing.onPointDown(
                    event,
                    element.id,
                    editing.point!,
                    handle,
                  );
                }}
                onClick={(event) => event.stopPropagation()}
              />
            ),
        )}
    </div>
  );
}
